import { growthGet } from '@/data/http';
import { deepCamel, str } from '@/data/adapt';

export type MarketingJobStatus = 'queued' | 'running' | 'completed' | 'failed' | string;

export interface MarketingJobStatusData {
  jobId: string;
  status: MarketingJobStatus;
  rawStatus?: string;
  jobType?: string;
  operation?: string;
  entityType?: string;
  entityId?: string;
  articleId?: string;
  landingPageId?: string;
  errorMessage?: string;
  errorCode?: string;
}

function adaptJobStatus(row: Record<string, unknown>): MarketingJobStatusData {
  const c = deepCamel<Record<string, unknown>>(row);
  const error =
    c.error && typeof c.error === 'object'
      ? deepCamel<Record<string, unknown>>(c.error as Record<string, unknown>)
      : null;

  return {
    jobId: str(c.jobId ?? c.id),
    status: str(c.status, 'queued'),
    ...(c.rawStatus != null ? { rawStatus: str(c.rawStatus) } : {}),
    ...(c.jobType != null ? { jobType: str(c.jobType) } : {}),
    ...(c.operation != null ? { operation: str(c.operation) } : {}),
    ...(c.entityType != null ? { entityType: str(c.entityType) } : {}),
    ...(c.entityId != null ? { entityId: str(c.entityId) } : {}),
    ...(c.articleId != null ? { articleId: str(c.articleId) } : {}),
    ...(c.landingPageId != null ? { landingPageId: str(c.landingPageId) } : {}),
    ...(c.errorMessage != null || error?.message != null
      ? { errorMessage: str(c.errorMessage ?? error?.message) }
      : {}),
    ...(error?.code != null ? { errorCode: str(error.code) } : {}),
  };
}

export async function fetchJobStatus(jobId: string): Promise<MarketingJobStatusData> {
  const row = await growthGet<Record<string, unknown>>(
    `/jobs/${encodeURIComponent(jobId)}/`,
  );
  return adaptJobStatus(row);
}

/** Light poll for async article/LP jobs — surfaces failure toasts without blocking UI. */
export async function watchJobFailure(
  jobId: string,
  onFailed: (message: string) => void,
  options?: { attempts?: number; delayMs?: number },
): Promise<void> {
  const attempts = options?.attempts ?? 8;
  const delayMs = options?.delayMs ?? 1500;
  const id = jobId.trim();
  if (!id) return;

  for (let i = 0; i < attempts; i++) {
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    try {
      const job = await fetchJobStatus(id);
      if (job.status === 'completed') return;
      if (job.status === 'failed') {
        onFailed(job.errorMessage || 'Background job failed');
        return;
      }
    } catch {
      // Ignore transient poll errors; next attempt may succeed.
    }
  }
}
